import React, { createContext, useEffect, useMemo, useState } from "react";
import { getLocalStorageUserSession } from "../services/secureStorage";
import { useInitComponents } from "zeus-front-design-system";
import { logger } from "./logger";

type ThemeTokens = {
  gray: string;
  loader: string;
  brand: string;
  grad1: string;
  grad2: string;
  grad3: string;
  errStart: string;
  errEnd: string;
  logoUrl?: string | null;
  companyId: string;
  companyName?: string;
  biometrics: boolean;
  fontColor: string;
};

const FALLBACK: ThemeTokens = {
  gray: "#999999",
  loader: "#21222c99",
  brand: "#614ac9",
  grad1: "#c8c0ec",
  grad2: "#b3e0ed",
  grad3: "#9effee",
  errStart: "#fcdada",
  errEnd: "#d94646",
  logoUrl: null,
  companyId: "",
  companyName: undefined,
  biometrics: false,
  fontColor: "#ffffff",
};

type CtxValue = ThemeTokens & {
  setBrand: (hex: string) => void;
};

export const ColorContext = createContext<CtxValue>({
  ...FALLBACK,
  setBrand: () => {},
});

type Props = { children: React.ReactNode };

export function ColorProvider({ children }: Props) {
  const [theme, setTheme] = useState<ThemeTokens>(FALLBACK);

  useInitComponents({
    backgroundColor: theme.brand,
    fontColor: "#ffffff",
  });

  useEffect(() => {
    (async () => {
      try {
        // ✅ ya regresa el objeto "decoded" (después del decrypt propio)
        const decoded: any = await getLocalStorageUserSession();
        if (!decoded) {
          applyCssVars(FALLBACK);
          setTheme(FALLBACK);
          return;
        }

        // Estructura según tu buildSessionFromLogin()
        const cfg = decoded?.data?.company?.configuration ?? {};
        const comp = decoded?.data?.company ?? {};
        const colors = cfg?.colors ?? {};
        const logoUrl = comp?.logo || null;

        const brand =
          cfg?.color ?? colors?.primary ?? decoded?.data?.company?.primary ?? FALLBACK.brand;
        const grad1 =
          cfg?.gradColor1 ?? cfg?.gradientStart ?? colors?.gradientStart ?? FALLBACK.grad1;
        const grad3 = cfg?.gradColor3 ?? cfg?.gradientEnd ?? colors?.gradientEnd ?? FALLBACK.grad3;
        const grad2 = cfg?.gradColor2 ?? FALLBACK.grad2;

        const ensure = (v: unknown, fb: string) => (typeof v === "string" && v.trim() ? v : fb);

        const next: ThemeTokens = {
          gray: ensure(cfg?.gray, FALLBACK.gray),
          loader: ensure(cfg?.loader, FALLBACK.loader),
          brand: ensure(brand, FALLBACK.brand),
          grad1: ensure(grad1, FALLBACK.grad1),
          grad2: ensure(grad2, FALLBACK.grad2),
          grad3: ensure(grad3, FALLBACK.grad3),
          errStart: ensure(cfg?.gradErrorStart, FALLBACK.errStart),
          errEnd: ensure(cfg?.gradErrorEnd, FALLBACK.errEnd),
          logoUrl: logoUrl ?? FALLBACK.logoUrl,
          companyId: String(comp?.id ?? comp?.id_company ?? FALLBACK.companyId),
          companyName: comp?.name ?? comp?.company_name ?? FALLBACK.companyName,
          biometrics: !!cfg?.biometrics,
          fontColor: ensure(cfg?.textColor, FALLBACK.fontColor),
        };

        applyCssVars(next);
        setTheme(next);
      } catch (e) {
        logger.error("ColorProvider: error leyendo sesión/tema:", e);
        applyCssVars(FALLBACK);
        setTheme(FALLBACK);
      }
    })();
  }, []);

  const setBrand = (hex: string) => {
    const next = { ...theme, brand: hex };
    applyCssVars(next);
    setTheme(next);
  };

  const value = useMemo<CtxValue>(() => ({ ...theme, setBrand }), [theme]);
  return <ColorContext.Provider value={value}>{children}</ColorContext.Provider>;
}

// Setea EXACTAMENTE las variables CSS que usa tu UI
function applyCssVars(t: ThemeTokens) {
  const root = document.documentElement;
  root.style.setProperty("--gray", t.gray);
  root.style.setProperty("--loader", t.loader);
  root.style.setProperty("--brand", t.brand);
  root.style.setProperty("--grad-color-1", t.grad1);
  root.style.setProperty("--grad-color-2", t.grad2);
  root.style.setProperty("--grad-color-3", t.grad3);
  root.style.setProperty("--grad-error-start", t.errStart);
  root.style.setProperty("--grad-error-end", t.errEnd);
  root.style.setProperty("--font", t.fontColor);
}

export const clearTheme = () => {
  applyCssVars(FALLBACK);
};
